import { i18n } from "@lingui/core";
import dayjs from "dayjs";
import { MRT_Row } from "mantine-react-table";
import * as XLSX from "xlsx";

import { GetBookingsItem } from "@/shared/api/generated/schemas";
import { countryListnbNO } from "@/shared/utils/countryList";
import { langugeListnbNO } from "@/shared/utils/languageList";

import { bookingPaymentFormLabels } from "./payment-form";
import { bookingPaymentFormDrinksLabels } from "./payment-form-drinks";
import { bookingPaymentStatusLabels } from "./payment-status";
import { bookingStatusLabels } from "./status";
import { bookingVisitCategoryLabels } from "./visit-category";

export function downloadBookingsAsExcelFile(rows: MRT_Row<GetBookingsItem>[]) {
  const rowData = rows.map((row) => row.original);

  // Define column configuration with headers and widths
  const baseColumns: { header: string; width: number }[] = [
    { header: "Bookingnummer", width: 10 },
    { header: "Dato opprettet", width: 14 }, // Date column - 100px
    { header: "Opprettet av", width: 10 },
    { header: "Dato oppdatert", width: 14 }, // Date column - 100px
    { header: "Oppdatert av", width: 10 },
    { header: "Bookingstatus", width: 10 },
    { header: "Lokasjon", width: 10 },
    { header: "Avdeling", width: 10 },
    { header: "Ankomst", width: 14 }, // Date column - 100px
    { header: "Avreise", width: 14 }, // Date column - 100px
    { header: "Besøkskategori", width: 10 },
    { header: "Kunde ID", width: 10 },
    { header: "Kunde", width: 10 },
    { header: "Betalingsform", width: 10 },
    { header: "Betalingsform drikke", width: 10 },
    { header: "Betalingsstatus", width: 10 },
    { header: "Sent til PowerOffice dato", width: 14 }, // Date column - 100px
    { header: "Gruppe", width: 10 },
    { header: "Kontaktperson", width: 10 },
    { header: "Kontaktperson mobil", width: 10 },
    { header: "Nasjonalitet", width: 10 },
    { header: "Språk", width: 10 },
    { header: "Forventet antall totalt", width: 10 },
    { header: "Forventet reiseleder/sjåfør", width: 10 },
    { header: "Ankommet totalt", width: 10 },
    { header: "Ankommet voksne", width: 10 },
    { header: "Ankommet reiseleder/sjåfør", width: 10 },
    { header: "Ankommet barn (gratis)", width: 10 },
    { header: "Ankommet barn (betalende)", width: 10 },
    { header: "Guide", width: 10 },
    { header: "Ekstern informasjon", width: 10 },
    { header: "Intern informasjon", width: 10 },
    { header: "Program", width: 10 },
  ];

  // Find maximum number of products across all bookings (only count actual products, not program items)
  const maxProducts = Math.max(
    ...rowData.map(
      (booking) =>
        booking.program?.filter((p) => p.productID !== null).length ?? 0,
    ),
  );

  // Add dynamic product columns
  const columns = [...baseColumns];
  for (let i = 1; i <= maxProducts; i++) {
    columns.push({ header: `Produkt ${i} ID`, width: 10 });
    columns.push({ header: `Produkt ${i} Navn`, width: 10 });
    columns.push({ header: `Produkt ${i} Start`, width: 14 }); // Date column - 100px
    columns.push({ header: `Produkt ${i} Slutt`, width: 14 }); // Date column - 100px
    columns.push({ header: `Produkt ${i} Pris`, width: 10 });
    columns.push({ header: `Produkt ${i} Antall`, width: 10 });
  }

  // Build aoaData array mapping each booking to the headers structure
  const aoaData = rowData.map((booking) => {
    const row = [
      createNumberCell(booking.id), // Bookingnummer - number
      createDateCell(booking.createdAt), // Dato opprettet - Cell Object with date formatting
      booking.createdBy
        ? `${booking.createdBy.first_name ?? ""} ${booking.createdBy.last_name ?? ""}`.trim()
        : "", // Opprettet av - string
      createDateCell(booking.updatedAt), // Dato oppdatert - Cell Object with date formatting
      booking.updatedBy
        ? `${booking.updatedBy.first_name ?? ""} ${booking.updatedBy.last_name ?? ""}`.trim()
        : "", // Oppdatert av - string
      booking.status ? i18n.t(bookingStatusLabels[booking.status]) : "", // Bookingstatus - string
      booking.location?.name ?? "", // Lokasjon - string
      booking.department?.name ?? "", // Avdeling - string
      createDateCell(booking.arrival), // Ankomst - Cell Object with date formatting
      createDateCell(booking.departure), // Avreise - Cell Object with date formatting
      booking.visitCategory
        ? i18n.t(bookingVisitCategoryLabels[booking.visitCategory])
        : "", // Besøkskategori - string
      createNumberCell(booking.customer?.id), // Kunde ID - number
      booking.customer?.name ?? "", // Kunde - string
      booking.paymentCategory
        ? i18n.t(bookingPaymentFormLabels[booking.paymentCategory])
        : "", // Betalingsform - string
      booking.paymentCategoryDrinks
        ? i18n.t(bookingPaymentFormDrinksLabels[booking.paymentCategoryDrinks])
        : "", // Betalingsform drikke - string
      booking.paymentStatus
        ? i18n.t(bookingPaymentStatusLabels[booking.paymentStatus])
        : "", // Betalingsstatus - string
      createDateCell(booking.sentToPowerOfficeDate), // Sent til PowerOffice dato - Cell Object with date formatting
      booking.group?.name ?? "", // Gruppe - string
      booking.group?.contactPerson ?? "", // Kontaktperson - string
      booking.group?.phone ?? "", // Kontaktperson mobil - string
      booking.group?.nationalities
        ?.map(
          (code) =>
            countryListnbNO.find((country) => country.value === code)?.label ??
            code,
        )
        .join(", ") ?? "", // Nasjonalitet - string
      booking.group?.languages
        ?.map(
          (code) =>
            langugeListnbNO.find((lang) => lang.value === code)?.label ?? code,
        )
        .join(", ") ?? "", // Språk - string
      createNumberCell(
        (booking.group?.guests?.booked ?? 0) +
          (booking.group?.travelLeader?.booked ?? 0),
      ), // Forventet antall totalt - number
      createNumberCell(booking.group?.travelLeader?.booked), // Forventet reiseleder/sjåfør - number
      createNumberCell(booking.group?.arrivals?.sum), // Ankommet totalt - number
      createNumberCell(booking.group?.arrivals?.adults), // Ankommet voksne - number
      createNumberCell(booking.group?.arrivals?.staff), // Ankommet reiseleder/sjåfør - number
      createNumberCell(booking.group?.arrivals?.childrenFree), // Ankommet barn (gratis) - number
      createNumberCell(booking.group?.arrivals?.childrenPaid), // Ankommet barn (betalende) - number
      booking.guide
        ? `${booking.guide.firstName} ${booking.guide.lastName ?? ""}`.trim()
        : "", // Guide - string
      booking.externalInformation ?? "", // Ekstern informasjon - string
      booking.internalInformation ?? "", // Intern informasjon - string
      // Program - only show program items (productID is null), formatted with time ranges
      booking.program
        ?.filter((p) => p.productID === null)
        .map((p) => {
          const name = p.comment ?? "";
          if (p.start && p.end) {
            const startTime = dayjs(p.start).format("HH:mm");
            const endTime = dayjs(p.end).format("HH:mm");
            return `${startTime}-${endTime} ${name}`;
          }
          return name;
        })
        .join("\n") ?? "", // Program - string
    ];

    // Add product columns - only actual products (productID is not null)
    const products = booking.program?.filter((p) => p.productID !== null) ?? [];
    for (let i = 0; i < maxProducts; i++) {
      const product = products[i];
      if (product) {
        row.push(product.productID ?? ""); // Produkt ID - string (UUID)
        row.push(product.productNameNO ?? product.productNameEN ?? ""); // Produkt Navn - string
        row.push(createDateCell(product.start)); // Produkt Start - time cell
        row.push(createDateCell(product.end)); // Produkt Slutt - time cell
        row.push(createCurrencyCell(product.price)); // Produkt Pris - currency cell
        row.push(createNumberCell(product.quantity)); // Produkt Antall - number cell
      } else {
        // Fill empty product slots
        row.push(""); // Produkt ID
        row.push(""); // Produkt Navn
        row.push(""); // Produkt Start
        row.push(""); // Produkt Slutt
        row.push(""); // Produkt Pris
        row.push(""); // Produkt Antall
      }
    }

    return row;
  });

  const headers = columns.map((col) => col.header);

  const data = [headers, ...aoaData];

  // Create workbook and worksheet
  const ws = XLSX.utils.aoa_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Bookings");

  // Set column widths from the column configuration
  ws["!cols"] = columns.map((col) => ({ wch: col.width }));

  // Export file
  XLSX.writeFile(wb, `bookings-${dayjs().format("DD.MM.YYYY")}.xlsx`);
}

// Helper function to create date cell objects
const createDateCell = (
  dateString: string | null | undefined,
): XLSX.CellObject | string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  return {
    t: "d", // Cell type: date
    v: date, // Value: Date object
    z: "d.m.yyyy hh:mm", // Format: Norwegian date format with time (HH for 24-hour format)
  };
};

// Helper function to create number cell objects
const createNumberCell = (
  value: number | null | undefined,
  format?: string,
): XLSX.CellObject | string => {
  if (value === null || value === undefined) return "";
  return {
    t: "n", // Cell type: number
    v: value, // Value: number
    z: format, // Optional number format
  };
};

// Helper function to create currency cell objects (Norwegian Kroner)
const createCurrencyCell = (
  value: number | null | undefined,
): XLSX.CellObject | string => {
  if (value === null || value === undefined) return "";
  return {
    t: "n", // Cell type: number
    v: value, // Value: number
    z: '#,##0.00 "kr"', // Norwegian currency format
  };
};
